import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  Show,
  SimpleShowLayout,
  useRecordContext,
  usePermissions,
  useListContext,
} from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { hasRole, type Permissions } from '../types';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
// apiFetch attaches the Bearer token and handles 401 refresh/retry. The raw
// fetch calls this file used before sent NO Authorization header (cookies
// only, which the API doesn't use), so every licensing API call 401'd.
import { apiFetch } from '../providers/api-rest-provider';
import {
  getSessionGeneration,
  getSessionRevision,
  subscribeSession,
} from '../providers/cognito';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LicenseRecord {
  id: string;
  sku: string;
  seatsPurchased: number;
  status: 'active' | 'expired' | 'cancelled';
  activatedAt?: string;
  expiresAt?: string;
  orderRef?: string;
}

interface SeatAllocation {
  orgPath: string;
  seatsAllocated: number;
}

interface UsageSummary {
  orgPath: string;
  seatsPurchased: number;
  seatsAllocated: number;
  seatsConsumed: number;
}

interface SessionBinding {
  key: string;
  revision: number;
  generation: number;
}

interface SessionOwnedState<T> {
  owner: string;
  licenseId: string;
  loading: boolean;
  data: T;
  error: string | null;
}

const sessionSnapshot = (): string => `${getSessionRevision()}:${getSessionGeneration()}`;

function useSessionBinding(): SessionBinding {
  const key = useSyncExternalStore(subscribeSession, sessionSnapshot, sessionSnapshot);
  const revision = getSessionRevision();
  const generation = getSessionGeneration();
  return useMemo(() => ({ key, revision, generation }), [key, revision, generation]);
}

const isSessionBindingCurrent = (binding: SessionBinding): boolean =>
  binding.revision === getSessionRevision() &&
  binding.generation === getSessionGeneration();

const canonicalOrgPath = (value: unknown): value is string =>
  typeof value === 'string' && /^\/(?:[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*)?$/.test(value);

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const isExactObject = (value: unknown, keys: readonly string[]): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const actual = Object.keys(value as Record<string, unknown>).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
};

function parseUsageSummary(value: unknown, expectedOrgPath = '/'): UsageSummary {
  const keys = ['orgPath', 'seatsPurchased', 'seatsAllocated', 'seatsConsumed'] as const;
  if (
    !isExactObject(value, keys) ||
    !canonicalOrgPath(value.orgPath) ||
    value.orgPath !== expectedOrgPath ||
    !isCount(value.seatsPurchased) ||
    !isCount(value.seatsAllocated) ||
    !isCount(value.seatsConsumed)
  ) {
    throw new Error('Licensing usage API returned an invalid response');
  }
  return {
    orgPath: value.orgPath,
    seatsPurchased: value.seatsPurchased,
    seatsAllocated: value.seatsAllocated,
    seatsConsumed: value.seatsConsumed,
  };
}

function parseSeatAllocations(value: unknown): SeatAllocation[] {
  if (!Array.isArray(value)) {
    throw new Error('License allocations API returned an invalid response');
  }
  const seen = new Set<string>();
  return value.map((row) => {
    if (
      !isExactObject(row, ['orgPath', 'seatsAllocated']) ||
      !canonicalOrgPath(row.orgPath) ||
      !isCount(row.seatsAllocated) ||
      seen.has(row.orgPath)
    ) {
      throw new Error('License allocations API returned an invalid response');
    }
    seen.add(row.orgPath);
    return { orgPath: row.orgPath, seatsAllocated: row.seatsAllocated };
  });
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  active: 'success',
  expired: 'error',
  cancelled: 'default',
};

const StatusChip = () => {
  const record = useRecordContext<LicenseRecord>();
  if (!record) return null;
  const status = record.status ?? 'active';
  return (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      size="small"
      color={STATUS_COLOR[status] ?? 'default'}
    />
  );
};
StatusChip.displayName = 'StatusChip';

// ---------------------------------------------------------------------------
// Expiry alert banner (non-dismissable)
// ---------------------------------------------------------------------------

const EXPIRY_WARN_DAYS = 60;

const ExpiryAlertBanner = ({ data }: { data: LicenseRecord[] }) => {
  const today = new Date();
  const expiring = data.filter((r) => {
    if (!r.expiresAt || r.status !== 'active') return false;
    const d = differenceInCalendarDays(parseISO(r.expiresAt), today);
    return d >= 0 && d <= EXPIRY_WARN_DAYS;
  });

  if (expiring.length === 0) return null;

  return (
    <Box mb={2}>
      {expiring.map((r) => (
        <Alert key={r.id} severity="warning" sx={{ mb: 1 }}>
          License <strong>{r.sku}</strong> expires on{' '}
          <strong>{format(parseISO(r.expiresAt!), 'yyyy-MM-dd')}</strong> —
          contact your contracting officer to renew.
        </Alert>
      ))}
    </Box>
  );
};
ExpiryAlertBanner.displayName = 'ExpiryAlertBanner';

// ---------------------------------------------------------------------------
// LicenseList
// ---------------------------------------------------------------------------

// Inner component that reads from ListContext to render the expiry banner
const LicenseListInner = () => {
  const { data } = useListContext<LicenseRecord>();
  const records: LicenseRecord[] = data ?? [];

  return (
    <>
      <ExpiryAlertBanner data={records} />
      <Datagrid rowClick="show">
        <TextField source="sku" label="SKU" />
        <TextField source="seatsPurchased" label="Seats Purchased" />
        <FunctionField label="Status" render={() => <StatusChip />} />
        <DateField source="activatedAt" label="Activated At" />
        <DateField source="expiresAt" label="Expires At" />
      </Datagrid>
    </>
  );
};
LicenseListInner.displayName = 'LicenseListInner';

export const LicenseList = () => {
  const { permissions } = usePermissions<Permissions>();
  const isPurchasingOfficer = hasRole(permissions, 'purchasing_officer');
  const isTenantAdmin = hasRole(permissions, 'tenant_admin');

  if (!isPurchasingOfficer && !isTenantAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">You do not have permission to view licenses.</Alert>
      </Box>
    );
  }

  return (
    // resource is explicit because this List also renders inside the custom
    // /licensing route, where no ResourceContext exists — without it,
    // react-admin throws at render and the whole page error-boundaries.
    <List
      resource="licenses"
      exporter={false}
      sort={{ field: 'sku', order: 'ASC' }}
      perPage={25}
    >
      <LicenseListInner />
    </List>
  );
};
LicenseList.displayName = 'LicenseList';

// ---------------------------------------------------------------------------
// Seat allocation tree view (used in LicenseShow and LicenseAdminPage)
// ---------------------------------------------------------------------------

interface AllocationRowProps {
  licenseId: string;
  row: SeatAllocation;
  allRows: SeatAllocation[];
  onChange: () => void;
}

const AllocationRow = ({ licenseId, row, allRows, onChange }: AllocationRowProps) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const childSum = allRows
    .filter((r) => {
      if (!r.orgPath.startsWith(row.orgPath + '/')) return false;
      const rest = r.orgPath.slice(row.orgPath.length + 1);
      return !rest.includes('/');
    })
    .reduce((acc, r) => acc + r.seatsAllocated, 0);

  const adjust = async (delta: number) => {
    const next = row.seatsAllocated + delta;
    if (next < 0) {
      setError('Allocation cannot be negative.');
      return;
    }
    if (next < childSum) {
      setError(
        `Cannot reduce below ${childSum} — sum of children's allocations.`,
      );
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await apiFetch(`licenses/${licenseId}/allocations`, {
        method: 'POST',
        body: JSON.stringify({ orgPath: row.orgPath, seatsAllocated: next }),
      });
      onChange();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <TableRow>
      <TableCell sx={{ pl: `${(row.orgPath.split('/').length - 1) * 16}px` }}>
        {row.orgPath}
      </TableCell>
      <TableCell>{row.seatsAllocated}</TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => adjust(-1)}
            disabled={loading}
            aria-label="Decrease seats"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => adjust(1)}
            disabled={loading}
            aria-label="Increase seats"
          >
            <AddIcon fontSize="small" />
          </IconButton>
          {loading && <CircularProgress size={14} />}
        </Stack>
        {error && (
          <Typography variant="caption" color="error" display="block">
            {error}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};
AllocationRow.displayName = 'AllocationRow';

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

const exportCsv = (
  orderRef: string,
  allocations: SeatAllocation[],
) => {
  const year = new Date().getFullYear().toString();
  const csv = buildAllocationsCsv(orderRef, allocations, year);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `license-allocations-${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const csvCell = (value: string | number): string =>
  `"${String(value).replace(/"/g, '""')}"`;

function buildAllocationsCsv(
  orderRef: string,
  allocations: SeatAllocation[],
  period = new Date().getFullYear().toString(),
): string {
  const validated = parseSeatAllocations(allocations);
  const header = 'orderRef,period,orgPath,seatsAllocated';
  const rows = validated.map((allocation) =>
    [orderRef, period, allocation.orgPath, allocation.seatsAllocated].map(csvCell).join(','),
  );
  return [header, ...rows].join('\n');
}

// ---------------------------------------------------------------------------
// AllocationTreePanel — fetches + renders allocations for a license
// ---------------------------------------------------------------------------

interface AllocationTreePanelProps {
  licenseId: string;
  orderRef?: string;
}

export const AllocationTreePanel = ({ licenseId, orderRef }: AllocationTreePanelProps) => {
  const session = useSessionBinding();
  const emptyUsage = useCallback(
    (): SessionOwnedState<UsageSummary | null> => ({
      owner: session.key,
      licenseId,
      loading: false,
      data: null,
      error: null,
    }),
    [licenseId, session.key],
  );
  const emptyAllocations = useCallback(
    (): SessionOwnedState<SeatAllocation[]> => ({
      owner: session.key,
      licenseId,
      loading: false,
      data: [],
      error: null,
    }),
    [licenseId, session.key],
  );
  const [usageState, setUsageState] = useState<SessionOwnedState<UsageSummary | null>>(
    emptyUsage,
  );
  const [allocationState, setAllocationState] = useState<SessionOwnedState<SeatAllocation[]>>(
    emptyAllocations,
  );
  const [newOrgPath, setNewOrgPath] = useState('');
  const [newSeats, setNewSeats] = useState<number | ''>('');
  const [addState, setAddState] = useState<SessionOwnedState<null>>({
    owner: session.key,
    licenseId,
    loading: false,
    data: null,
    error: null,
  });
  const activeUsage = useRef<{ id: number; controller: AbortController } | null>(null);
  const activeAllocations = useRef<{ id: number; controller: AbortController } | null>(null);
  const nextUsageId = useRef(0);
  const nextAllocationId = useRef(0);
  const currentView = useRef({ owner: session.key, licenseId });
  currentView.current = { owner: session.key, licenseId };

  const ownsCurrentView = useCallback(
    (binding: SessionBinding, requestedLicenseId: string): boolean =>
      isSessionBindingCurrent(binding) &&
      currentView.current.owner === binding.key &&
      currentView.current.licenseId === requestedLicenseId,
    [],
  );

  const fetchUsage = useCallback(async () => {
    const binding = session;
    const requestedLicenseId = licenseId;
    if (!ownsCurrentView(binding, requestedLicenseId)) return;
    const controller = new AbortController();
    const requestId = ++nextUsageId.current;
    activeUsage.current?.controller.abort();
    activeUsage.current = { id: requestId, controller };
    setUsageState({
      owner: binding.key,
      licenseId: requestedLicenseId,
      loading: true,
      data: null,
      error: null,
    });
    try {
      const value = await apiFetch('licenses/usage?orgPath=/', { signal: controller.signal });
      const data = parseUsageSummary(value);
      if (
        controller.signal.aborted ||
        activeUsage.current?.id !== requestId ||
        !ownsCurrentView(binding, requestedLicenseId)
      ) return;
      setUsageState({
        owner: binding.key,
        licenseId: requestedLicenseId,
        loading: false,
        data,
        error: null,
      });
    } catch (e: unknown) {
      if (
        controller.signal.aborted ||
        activeUsage.current?.id !== requestId ||
        !ownsCurrentView(binding, requestedLicenseId)
      ) return;
      setUsageState({
        owner: binding.key,
        licenseId: requestedLicenseId,
        loading: false,
        data: null,
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      if (activeUsage.current?.id === requestId) activeUsage.current = null;
    }
  }, [licenseId, ownsCurrentView, session]);

  const fetchAllocations = useCallback(async () => {
    const binding = session;
    const requestedLicenseId = licenseId;
    if (!ownsCurrentView(binding, requestedLicenseId)) return;
    const controller = new AbortController();
    const requestId = ++nextAllocationId.current;
    activeAllocations.current?.controller.abort();
    activeAllocations.current = { id: requestId, controller };
    setAllocationState({
      owner: binding.key,
      licenseId: requestedLicenseId,
      loading: true,
      data: [],
      error: null,
    });
    try {
      const value = await apiFetch(`licenses/${requestedLicenseId}/allocations`, {
        signal: controller.signal,
      });
      const data = parseSeatAllocations(value);
      if (
        controller.signal.aborted ||
        activeAllocations.current?.id !== requestId ||
        !ownsCurrentView(binding, requestedLicenseId)
      ) return;
      setAllocationState({
        owner: binding.key,
        licenseId: requestedLicenseId,
        loading: false,
        data,
        error: null,
      });
    } catch (e: unknown) {
      if (
        controller.signal.aborted ||
        activeAllocations.current?.id !== requestId ||
        !ownsCurrentView(binding, requestedLicenseId)
      ) return;
      setAllocationState({
        owner: binding.key,
        licenseId: requestedLicenseId,
        loading: false,
        data: [],
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      if (activeAllocations.current?.id === requestId) activeAllocations.current = null;
    }
  }, [licenseId, ownsCurrentView, session]);

  const ownsState = (state: SessionOwnedState<unknown>): boolean =>
    state.owner === session.key && state.licenseId === licenseId;
  const visibleUsage = ownsState(usageState) ? usageState : emptyUsage();
  const visibleAllocations = ownsState(allocationState) ? allocationState : emptyAllocations();
  const visibleAddState = ownsState(addState)
    ? addState
    : { owner: session.key, licenseId, loading: false, data: null, error: null };
  const { data: usage, loading: loadingUsage, error: usageError } = visibleUsage;
  const {
    data: allocations,
    loading: loadingAlloc,
    error: allocError,
  } = visibleAllocations;
  const { loading: addLoading, error: addError } = visibleAddState;

  const addAllocation = async () => {
    if (!newOrgPath.trim() || newSeats === '' || newSeats < 0) {
      setAddState({
        owner: session.key,
        licenseId,
        loading: false,
        data: null,
        error: 'Org path and a valid seat count are required.',
      });
      return;
    }
    const binding = session;
    const requestedLicenseId = licenseId;
    setAddState({
      owner: binding.key,
      licenseId: requestedLicenseId,
      loading: true,
      data: null,
      error: null,
    });
    try {
      await apiFetch(`licenses/${requestedLicenseId}/allocations`, {
        method: 'POST',
        body: JSON.stringify({ orgPath: newOrgPath.trim(), seatsAllocated: newSeats }),
      });
      if (!ownsCurrentView(binding, requestedLicenseId)) return;
      setNewOrgPath('');
      setNewSeats('');
      await fetchAllocations();
    } catch (e: unknown) {
      if (!ownsCurrentView(binding, requestedLicenseId)) return;
      setAddState({
        owner: binding.key,
        licenseId: requestedLicenseId,
        loading: false,
        data: null,
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      if (ownsCurrentView(binding, requestedLicenseId)) {
        setAddState((current) =>
          current.owner === binding.key && current.licenseId === requestedLicenseId
            ? { ...current, loading: false }
            : current,
        );
      }
    }
  };

  useEffect(() => {
    currentView.current = { owner: session.key, licenseId };
    activeUsage.current?.controller.abort();
    activeAllocations.current?.controller.abort();
    setUsageState(emptyUsage());
    setAllocationState(emptyAllocations());
    setAddState({
      owner: session.key,
      licenseId,
      loading: false,
      data: null,
      error: null,
    });
    void fetchUsage();
    void fetchAllocations();
    return () => {
      activeUsage.current?.controller.abort();
      activeAllocations.current?.controller.abort();
      activeUsage.current = null;
      activeAllocations.current = null;
      if (
        currentView.current.owner === session.key &&
        currentView.current.licenseId === licenseId
      ) {
        currentView.current = { owner: '', licenseId: '' };
      }
    };
  }, [emptyAllocations, emptyUsage, fetchAllocations, fetchUsage, licenseId, session.key]);

  return (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>
        Seat Usage Summary
      </Typography>

      {loadingUsage && <CircularProgress size={20} sx={{ mb: 1 }} />}
      {usageError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Usage load error: {usageError}
        </Alert>
      )}
      {usage && (
        <Stack direction="row" gap={3} mb={2}>
          <Typography variant="body2">
            <strong>Purchased:</strong> {usage.seatsPurchased}
          </Typography>
          <Typography variant="body2">
            <strong>Allocated:</strong> {usage.seatsAllocated}
          </Typography>
          <Typography variant="body2">
            <strong>Consumed:</strong> {usage.seatsConsumed}
          </Typography>
        </Stack>
      )}

      <Typography variant="h6" gutterBottom>
        Seat Allocations by Org Unit
      </Typography>

      {loadingAlloc && <CircularProgress size={20} sx={{ mb: 1 }} />}
      {allocError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Allocations load error: {allocError}
        </Alert>
      )}

      {allocations.length > 0 && (
        <>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Org Path</TableCell>
                <TableCell>Seats Allocated</TableCell>
                <TableCell>Adjust</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((row) => (
                <AllocationRow
                  key={row.orgPath}
                  licenseId={licenseId}
                  row={row}
                  allRows={allocations}
                  onChange={fetchAllocations}
                />
              ))}
            </TableBody>
          </Table>

          <Box mt={2} mb={1}>
            <Typography variant="subtitle2" gutterBottom>Add Allocation</Typography>
            <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
              <MuiTextField
                label="Org Path"
                value={newOrgPath}
                onChange={(e) => setNewOrgPath(e.target.value)}
                size="small"
                placeholder="/aetc/2af"
                sx={{ minWidth: 200 }}
                disabled={addLoading}
              />
              <MuiTextField
                label="Seats"
                type="number"
                value={newSeats}
                onChange={(e) => setNewSeats(e.target.value === '' ? '' : Number(e.target.value))}
                size="small"
                sx={{ width: 100 }}
                disabled={addLoading}
                inputProps={{ min: 0 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={addAllocation}
                disabled={addLoading || !newOrgPath.trim() || newSeats === ''}
                startIcon={addLoading ? <CircularProgress size={14} /> : undefined}
              >
                Add
              </Button>
            </Stack>
            {addError && (
              <Typography variant="caption" color="error" display="block" mt={0.5}>
                {addError}
              </Typography>
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={() => exportCsv(orderRef ?? licenseId, allocations)}
          >
            Export CSV
          </Button>
        </>
      )}

      {!loadingAlloc && allocations.length === 0 && !allocError && (
        <Typography variant="body2" color="text.secondary">
          No allocations found for this license.
        </Typography>
      )}
    </Box>
  );
};
AllocationTreePanel.displayName = 'AllocationTreePanel';

// ---------------------------------------------------------------------------
// LicenseShow
// ---------------------------------------------------------------------------

const LicenseShowContent = () => {
  const record = useRecordContext<LicenseRecord>();
  if (!record) return null;

  return (
    <>
      <SimpleShowLayout>
        <TextField source="sku" label="SKU" />
        <TextField source="seatsPurchased" label="Seats Purchased" />
        <FunctionField label="Status" render={() => <StatusChip />} />
        <DateField source="activatedAt" label="Activated At" />
        <DateField source="expiresAt" label="Expires At" />
        <TextField source="orderRef" label="Order Reference" />
      </SimpleShowLayout>
      <Box px={2} pb={3}>
        <AllocationTreePanel licenseId={record.id} orderRef={record.orderRef} />
      </Box>
    </>
  );
};
LicenseShowContent.displayName = 'LicenseShowContent';

export const LicenseShow = () => (
  <Show>
    <LicenseShowContent />
  </Show>
);
LicenseShow.displayName = 'LicenseShow';

// ---------------------------------------------------------------------------
// ActivationCodeRedemption
// ---------------------------------------------------------------------------

export const ActivationCodeRedemption = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const redeem = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      const json = (await apiFetch('activation-codes/redeem', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim() }),
      })) as { seatsAdded?: number; message?: string };
      setSuccess(
        `Code redeemed — ${json.seatsAdded ?? 0} seats added to your license`,
      );
      setCode('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Redeem Activation Code" />
      <CardContent>
        <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
          <MuiTextField
            label="Activation Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') redeem();
            }}
          />
          <Button
            variant="contained"
            onClick={redeem}
            disabled={loading || !code.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            Redeem
          </Button>
        </Stack>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
ActivationCodeRedemption.displayName = 'ActivationCodeRedemption';

// ---------------------------------------------------------------------------
// LicenseAdminPage — full combined page
// ---------------------------------------------------------------------------

export const LicenseAdminPage = () => {
  const { permissions } = usePermissions<Permissions>();
  const isPurchasingOfficer = hasRole(permissions, 'purchasing_officer');
  const isTenantAdmin = hasRole(permissions, 'tenant_admin');

  if (!isPurchasingOfficer && !isTenantAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">You do not have permission to access Licensing.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Licensing
      </Typography>

      <Box mb={4}>
        <ActivationCodeRedemption />
      </Box>

      <Typography variant="h5" gutterBottom>
        License List
      </Typography>
      <LicenseList />
    </Box>
  );
};
LicenseAdminPage.displayName = 'LicenseAdminPage';
